import PhoneInput from '../ui/PhoneInput';
import { METHOD_LABELS, emptyEntry } from '../../lib/contactMethods';

// BL-177 PR C: one row shows; a "+" beside it reveals another entry. Every entry has a label (Mobile / Work / Home / Other) and exactly
// one entry per kind is Primary (the default for texts and emails). Used by the contact card, Add Client and the thread form.
//
//   kind      'phone' | 'email' | 'address'
//   entries   [{ id, label, value, is_primary }]
//   onChange  (nextEntries) => void   (Primary is kept to exactly one here; blank rows are dropped when saving)
const inputCls = 'w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)] ' +
    'placeholder:text-[var(--ink-muted)] outline-none focus:border-brand-500';

const PLACEHOLDER = { phone: undefined, email: 'name@example.com', address: 'Street, city, state, zip' };
const NOUN = { phone: 'phone', email: 'email', address: 'address' };

export default function LabeledMultiField({ kind, entries, onChange, disabled = false, ariaLabel, className = '', idPrefix }) {
    const rows = entries && entries.length ? entries : [{ ...emptyEntry(kind), is_primary: true }];
    const noun = NOUN[kind];
    const lastIsBlank = !(rows[rows.length - 1].value || '').trim();
    const canAdd = !disabled && !lastIsBlank;

    const update = (index, patch) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    const makePrimary = (index) => onChange(rows.map((row, i) => ({ ...row, is_primary: i === index })));
    const remove = (index) => {
        const next = rows.filter((_, i) => i !== index);
        if (next.length && !next.some(r => r.is_primary)) next[0] = { ...next[0], is_primary: true };
        onChange(next);
    };
    const add = () => {
        onChange([...rows, { ...emptyEntry(kind), is_primary: false }]);
    };

    return (
        <div className={className} data-testid={`multi-${kind}`}>
            {rows.map((row, index) => (
                <div key={row.id || `new-${index}`} className="mb-1.5 flex flex-wrap items-center gap-1.5">
                    <select
                        className={`${inputCls} !w-[5.5rem] shrink-0`}
                        aria-label={`${ariaLabel || noun} label ${index + 1}`}
                        value={row.label}
                        disabled={disabled}
                        onChange={event => update(index, { label: event.target.value })}
                    >
                        {METHOD_LABELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                    <div className="min-w-[8rem] flex-1">
                        {kind === 'phone' ? (
                            <PhoneInput
                                className={inputCls}
                                value={row.value}
                                id={index === 0 ? idPrefix : undefined}
                                aria-label={`${ariaLabel || 'Phone'} ${index + 1}`}
                                disabled={disabled}
                                onChange={value => update(index, { value })}
                            />
                        ) : (
                            <input
                                className={inputCls}
                                type={kind === 'email' ? 'email' : 'text'}
                                id={index === 0 ? idPrefix : undefined}
                                value={row.value}
                                placeholder={PLACEHOLDER[kind]}
                                aria-label={`${ariaLabel || noun} ${index + 1}`}
                                disabled={disabled}
                                onChange={event => update(index, { value: event.target.value })}
                            />
                        )}
                    </div>
                    {rows.length > 1 && (
                        <label className="flex shrink-0 items-center gap-1 text-xs text-[var(--ink-muted)]">
                            <input
                                type="radio"
                                name={`primary-${kind}-${ariaLabel || ''}`}
                                checked={!!row.is_primary}
                                disabled={disabled}
                                onChange={() => makePrimary(index)}
                                aria-label={`Primary ${noun} ${index + 1}`}
                            />
                            Primary
                        </label>
                    )}
                    {rows.length > 1 && !disabled && (
                        <button
                            type="button"
                            className="shrink-0 rounded px-1.5 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
                            aria-label={`Remove ${noun} ${index + 1}`}
                            onClick={() => remove(index)}
                        >×</button>
                    )}
                    {index === rows.length - 1 && (
                        <button
                            type="button"
                            className="shrink-0 rounded-full border border-[var(--surface-border)] px-2 text-sm font-semibold text-[var(--ink)] enabled:hover:border-brand-500 disabled:opacity-40"
                            aria-label={`Add another ${noun}`}
                            title={canAdd ? `Add another ${noun}` : `Fill this ${noun} first`}
                            disabled={!canAdd}
                            onClick={add}
                        >+</button>
                    )}
                </div>
            ))}
        </div>
    );
}
